import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface HelpTipProps {
    text: string;
}

const PANEL_MAX_WIDTH = 320;
const VIEWPORT_MARGIN = 12;

interface PanelPosition {
    top: number;
    left: number;
    width: number;
}

/**
 * Small "?" button that toggles an explanation popover on click/tap.
 * Rendered via portal with fixed positioning so it works inside
 * scrollable modals and never overflows the viewport on mobile.
 */
const HelpTip: React.FC<HelpTipProps> = ({ text }) => {
    const { t } = useTranslation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<PanelPosition | null>(null);

    const close = () => setPosition(null);

    const toggle = (e: React.MouseEvent) => {
        // Prevent a wrapping <label> from toggling its checkbox/radio
        e.preventDefault();
        e.stopPropagation();
        if (position) {
            close();
            return;
        }
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const width = Math.min(PANEL_MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
        const left = Math.min(
            Math.max(rect.left + rect.width / 2 - width / 2, VIEWPORT_MARGIN),
            window.innerWidth - width - VIEWPORT_MARGIN
        );
        setPosition({ top: rect.bottom + 8, left, width });
    };

    useEffect(() => {
        if (!position) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
                return;
            }
            close();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [position]);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={toggle}
                aria-expanded={!!position}
                aria-label={t('components.helpTip.showHelp')}
                title={t('components.helpTip.showHelp')}
                className="ml-1.5 align-middle text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
            >
                <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
            {position && createPortal(
                <div
                    ref={panelRef}
                    role="tooltip"
                    className="glass-dropdown fixed z-[70] p-3 text-sm leading-relaxed whitespace-pre-line"
                    style={{
                        top: position.top,
                        left: position.left,
                        width: position.width,
                        maxHeight: `calc(100vh - ${position.top + VIEWPORT_MARGIN}px)`,
                        overflowY: 'auto'
                    }}
                >
                    {text}
                </div>,
                document.body
            )}
        </>
    );
};

export default HelpTip;
